import React, { useMemo } from "react";
import { RotateCcw } from "lucide-react";

import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
	FAMILIAS,
	GRUPOS_DE_TOKENS,
	RAIOS,
	SOMBRAS,
	hexParaHsl,
	hslParaHex,
	lerPadroes,
	variaveisDoEstilo,
} from "../../helpers/estiloVisual";
import { cn } from "../../lib/utils";

const ATALHOS_DE_INTENSIDADE = [
	{ valor: 0, nome: "Cinza" },
	{ valor: 50, nome: "Suave" },
	{ valor: 100, nome: "Padrão" },
	{ valor: 135, nome: "Vivo" },
];

// Escolha entre poucas opções, todas à vista.
export const Segmentos = ({ opcoes, valor, aoMudar, rotulo }) => (
	<div className="inline-flex flex-wrap rounded-lg border bg-muted/40 p-0.5" role="radiogroup" aria-label={rotulo}>
		{opcoes.map((o) => (
			<button
				key={o.id}
				type="button"
				role="radio"
				aria-checked={valor === o.id}
				onClick={() => aoMudar(o.id)}
				className={cn(
					"rounded-md px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
					valor === o.id ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
				)}
			>
				{o.nome}
			</button>
		))}
	</div>
);

// Os campos do tema do estilo (cartões, cores, intensidade), usados pelo editor
// do estilo e pela aba "Tema" do inspetor nas páginas. `Secao` é a moldura de
// cada grupo, que cada lugar desenha do seu jeito; `mudar(parcial)` funde no
// estilo. As grades seguem a largura de quem contém (@container), não a tela:
// no inspetor estreito fica tudo em uma coluna.
const CamposDoTema = ({ estilo, mudar, modo, Secao, acaoDasCores }) => {
	const escuro = modo === "escuro";
	const tokens = estilo[modo] || {};
	const padroes = lerPadroes()[modo];

	const mudarToken = (chave, valor) =>
		mudar({ [modo]: Object.fromEntries(Object.entries({ ...tokens, [chave]: valor }).filter(([, v]) => v)) });

	// Sombra e cantos valem para os dois modos: é uma escolha só na tela.
	const mudarNosDois = (chave, claro, escuroValor) => {
		const tirarVazio = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v));
		mudar({
			claro: tirarVazio({ ...estilo.claro, [chave]: claro }),
			escuro: tirarVazio({ ...estilo.escuro, [chave]: escuroValor }),
		});
	};

	const sombraAtual =
		SOMBRAS.find((s) => (estilo.claro?.["sombra-cartao"] || "") === s.claro)?.id ?? "personalizada";
	const raioAtual = estilo.claro?.radius || "";

	const paleta = estilo.paleta || {};
	const geral = paleta.geral ?? 100;
	const familias = paleta.familias || {};
	const mudarPaleta = (parcial) => mudar({ paleta: { ...paleta, ...parcial } });

	// Só as famílias que o sistema usa: o Tailwind só gera a variável da cor
	// que alguma tela usa. Mexer na que não existe não mudaria nada.
	const familiasEmUso = useMemo(() => FAMILIAS.filter(({ id: f }) => lerPadroes().claro[`color-${f}-500`]), []);
	const amostras = useMemo(() => variaveisDoEstilo(estilo, escuro, true), [estilo, escuro]);

	return (
		<>
			<Secao
				titulo="Cartões"
				descricao="Para quem acha que os blocos se misturam com o fundo: sombra, cantos e, nas cores abaixo, fundo da página e bordas."
			>
				<div className="@container">
					<div className="grid gap-5 @lg:grid-cols-2">
						<div className="space-y-2">
							<Label>Sombra dos cartões</Label>
							<Segmentos
								rotulo="Sombra dos cartões"
								opcoes={[
									...SOMBRAS,
									...(sombraAtual === "personalizada" ? [{ id: "personalizada", nome: "Personalizada" }] : []),
								]}
								valor={sombraAtual}
								aoMudar={(s) => {
									const preset = SOMBRAS.find((p) => p.id === s);
									if (preset) mudarNosDois("sombra-cartao", preset.claro, preset.escuro);
								}}
							/>
						</div>
						<div className="space-y-2">
							<Label>Cantos</Label>
							<Segmentos
								rotulo="Cantos"
								opcoes={RAIOS.map((r) => ({ id: r.valor, nome: r.nome }))}
								valor={raioAtual}
								aoMudar={(r) => mudarNosDois("radius", r, r)}
							/>
						</div>
					</div>
				</div>
			</Secao>

			<Secao
				titulo="Cores do tema"
				descricao="Cada modo tem as suas. O que ficar no padrão acompanha o sistema."
				acao={acaoDasCores}
			>
				<div className="@container">
					<div className="grid gap-6 @xl:grid-cols-2">
						{GRUPOS_DE_TOKENS.map((grupo) => (
							<div key={grupo.titulo} className="space-y-2">
								<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{grupo.titulo}</p>
								{grupo.tokens.map(({ chave, nome }) => {
									const proprio = tokens[chave];
									return (
										<div key={chave} className="flex items-center gap-3 rounded-lg border px-3 py-2">
											<input
												type="color"
												aria-label={nome}
												className="h-8 w-10 shrink-0 cursor-pointer rounded border bg-transparent p-0.5"
												value={hslParaHex(proprio || padroes[chave])}
												onChange={(e) => mudarToken(chave, hexParaHsl(e.target.value))}
											/>
											<div className="min-w-0 flex-1">
												<p className="truncate text-sm">{nome}</p>
												<p className="text-xs text-muted-foreground">{proprio ? "Personalizada" : "Padrão do sistema"}</p>
											</div>
											{proprio ? (
												<Button
													size="icon"
													variant="ghost"
													className="h-8 w-8"
													aria-label={`Voltar ${nome} ao padrão`}
													onClick={() => mudarToken(chave, "")}
												>
													<RotateCcw className="h-4 w-4" />
												</Button>
											) : null}
										</div>
									);
								})}
							</div>
						))}
					</div>
				</div>
			</Secao>

			<Secao
				titulo="Indicadores coloridos"
				descricao="Selos, ícones de status, etiquetas e números coloridos. 0% deixa cinza, 100% é como o sistema é hoje, acima disso fica mais vivo. O tom e a claridade não mudam, então o texto continua legível."
			>
				<div className="space-y-5">
					<div className="space-y-2">
						<div className="flex flex-wrap items-center gap-3">
							<Label htmlFor={`ev-geral-${modo}`} className="flex-1">
								Intensidade geral
							</Label>
							<span className="w-12 text-right text-sm tabular-nums">{geral}%</span>
						</div>
						<input
							id={`ev-geral-${modo}`}
							type="range"
							min={0}
							max={200}
							step={5}
							value={geral}
							onChange={(e) => mudarPaleta({ geral: Number(e.target.value) })}
							className="w-full accent-primary"
						/>
						<div className="flex flex-wrap gap-1.5">
							{ATALHOS_DE_INTENSIDADE.map((a) => (
								<Button
									key={a.valor}
									size="sm"
									variant={geral === a.valor ? "secondary" : "outline"}
									onClick={() => mudarPaleta({ geral: a.valor })}
								>
									{a.nome}
								</Button>
							))}
						</div>
					</div>

					<div>
						<p className="mb-2 text-xs text-muted-foreground">
							Por cor — para manter uma cor que tem significado (o vermelho de erro, por exemplo) mesmo com o
							resto apagado.
						</p>
						<div className="@container">
							<div className="grid gap-2 @lg:grid-cols-2 @4xl:grid-cols-3">
								{familiasEmUso.map(({ id: f, nome }) => {
									const proprio = familias[f];
									const valor = proprio ?? geral;
									return (
										<div key={f} className="flex items-center gap-2.5 rounded-lg border px-3 py-2">
											<span
												className="h-5 w-5 shrink-0 rounded-full border"
												style={{ background: amostras[`color-${f}-500`] }}
											/>
											<div className="min-w-0 flex-1">
												<div className="flex items-center justify-between gap-2">
													<span className="truncate text-sm">{nome}</span>
													<span className={cn("text-xs tabular-nums", proprio === undefined && "text-muted-foreground")}>
														{proprio === undefined ? `geral (${geral}%)` : `${valor}%`}
													</span>
												</div>
												<input
													type="range"
													min={0}
													max={200}
													step={5}
													value={valor}
													aria-label={`Intensidade de ${nome}`}
													onChange={(e) => mudarPaleta({ familias: { ...familias, [f]: Number(e.target.value) } })}
													className={cn("w-full accent-primary", proprio === undefined && "opacity-50")}
												/>
											</div>
											{proprio !== undefined ? (
												<Button
													size="icon"
													variant="ghost"
													className="h-7 w-7"
													aria-label={`${nome} segue a intensidade geral`}
													onClick={() =>
														mudarPaleta({
															familias: Object.fromEntries(Object.entries(familias).filter(([k]) => k !== f)),
														})
													}
												>
													<RotateCcw className="h-3.5 w-3.5" />
												</Button>
											) : null}
										</div>
									);
								})}
							</div>
						</div>
					</div>
				</div>
			</Secao>
		</>
	);
};

export default CamposDoTema;
